/**
 * O que o profissional disse sobre si — nome, papel e objetivo — lido do
 * TEXTO do turno dele (spec-009, US-3/AC-3.2).
 *
 * ★ **A leitura é do TEXTO, e é por isso que fala e texto valem igual**
 * (AC-3.1): a transcrição de uma fala entra na conversa como a MESMA mensagem
 * de `role: 'user'` que a digitação produz. Um só caminho de extração, sem
 * ramo por origem — quem prefere digitar não fica de fora por construção, não
 * por um `if` que alguém lembrou de escrever.
 *
 * ★ **Isto NUNCA grava nada.** Ele devolve uma PROPOSTA para o cartão de
 * confirmação mostrar; quem grava é o clique (AC-3.2/AC-3.3). A tool `memory`
 * do modelo — que é carve-out de `WRITE_TOOL_NAMES` e grava sem cartão
 * (`constants/memoria.py`, objeção do advisor na spec-009) — não participa
 * deste caminho em ponto nenhum.
 *
 * ⚠️ **É heurística, e é assumida como tal.** Nenhuma régua de palavras acerta
 * toda apresentação humana, e a transcrição da fala ainda erra nome próprio.
 * As duas proteções são de DESENHO, não de precisão: o cartão é EDITÁVEL antes
 * de confirmar, e sem clique nada é gravado. Errar a proposta custa uma
 * correção de campo; gravar sem clique custaria a confiança na plataforma.
 *
 * ★ **[18/09/2026] TRÊS RÉGUAS NOVAS, e todas nasceram do mesmo cartão.** O
 * owner perguntou *"O que preciso fazer hoje?"* e recebeu uma proposta de
 * perfil com `objetivo: "fazer hoje"` — o rabo da própria pergunta — e
 * `nome: "Primeiro"`, o primeiro nome da SESSÃO, que ele nunca disse. O turno
 * lido estava certo (era a última mensagem dele); o que estava frouxo era o
 * critério de "há apresentação":
 *
 * 1. **Pergunta não é apresentação** — a frase interrogativa é descartada
 *    ANTES de qualquer extração. Quem pergunta está pedindo, não se contando.
 * 2. **Fragmento não é objetivo** — "fazer hoje", "migrar uma": pedaço cortado
 *    pela pontuação ou pela hesitação do ditado não vira objetivo de vida.
 * 3. **Marcador fraco só vale acompanhado** — "quero"/"preciso" abrem um
 *    pedido ("quero ver o PL do Ilo") com a mesma cara com que abrem uma
 *    apresentação. Sozinhos, só valem onde a apresentação foi PEDIDA — o
 *    primeiro contato, cujo roteiro termina em *"agora fale um pouco sobre
 *    você e seu objetivo com a plataforma"* (`roteiro.js::FECHAMENTO`).
 *
 * ⚠️ **A régua DESISTE em vez de costurar.** A fala ditada daquele mesmo teste
 * ("quero migrar uma... Meus clientes pra F fixo") tem uma apresentação de
 * verdade dentro, e ainda assim não vira proposta: o que dá para tirar dela é
 * um fragmento, e emendar os pedaços seria escrever o que a pessoa não disse.
 * Não propor custa um cartão a menos — a tela de Memória da IA continua
 * aberta. Propor errado custaria a confiança no que a plataforma diz guardar.
 */
import { normalizeText } from '@utils/Format'

/** As três chaves que a proposta grava em `perfil.extras` — ver o mixin. */
export const CHAVES_DA_APRESENTACAO = Object.freeze({
  NOME: 'nome',
  PAPEL: 'papel',
  OBJETIVO: 'objetivo',
})

/**
 * Teto de UM valor de `extras`, do contrato do backend
 * (`PerfilProfissional.extras: dict[str, str]`, "valor ≤ 200 chars" —
 * `memoria-perfil-form.vue::MAX_EXTRA_VALUE_LEN`). Cortar aqui é o que impede
 * uma fala longa de virar um 400 no clique de gravar.
 */
export const MAX_CHARS_DO_VALOR = 200

// Papéis do mercado que este produto atende. Lista fechada de propósito: é ela
// que separa "sou o Gabriel" (nome) de "sou consultor" (papel) — o mesmo
// marcador introduz os dois, e sem a lista o papel viraria um nome próprio
// esquisito no cartão.
const PAPEIS = [
  'consultor', 'consultora', 'assessor', 'assessora', 'planejador', 'planejadora',
  'gestor', 'gestora', 'analista', 'advisor', 'agente autonomo', 'socio', 'socia',
  'especialista', 'diretor', 'diretora', 'gerente', 'economista', 'contador', 'contadora',
]

// Qualificadores que costumam vir colados ao papel ("consultor DE INVESTIMENTOS").
// Sem eles o cartão proporia só "consultor", que diz menos do que a pessoa disse.
//
// ⚠️ UMA palavra depois da preposição, nunca duas: com duas, "sou consultor de
// investimentos E quero..." levava o conectivo junto e o cartão propunha o
// papel "consultor de investimentos e" (medido pelo teste). Um papel de três
// palavras perde a última — e o campo é editável no cartão, que é onde esse
// tipo de perda se conserta.
const QUALIFICADOR = '(?:\\s+(?:de|da|do|em)\\s+[a-zà-ú]+|\\s+financeir[oa])?'

// Marcadores de NOME em DOIS grupos, e a diferença entre eles é o que separa
// "sou o Rafael" de "sou responsável pelo relatório".
//
// FORTES — quem diz "meu nome é" está dizendo um nome. Aceitam qualquer caixa,
// porque quem digita depressa escreve "me chamo gabriel".
const MARCADORES_FORTES_DE_NOME = [
  'meu nome (?:é|eh|e)',
  'me chamo',
  'pode me chamar de',
  'me chame de',
  'aqui (?:é|eh|e) (?:o|a)',
]

// FRACOS — `sou` introduz nome, papel e qualquer adjetivo do idioma. Aqui a
// INICIAL MAIÚSCULA é exigida: sem ela, "sou responsável pelo relatório"
// gravaria `nome: "responsável"` (medido pelo teste). O custo é que "sou o
// gabriel" em minúsculas não vira nome — e aí o cartão propõe o nome da
// SESSÃO, que é melhor palpite que um adjetivo.
const MARCADORES_FRACOS_DE_NOME = [
  'sou (?:o|a)',
  'eu sou (?:o|a)',
  'sou',
  'eu sou',
]

// Marcadores de OBJETIVO em DOIS grupos — o que a pessoa veio fazer. O trecho
// vai até o fim da frase, porque objetivo é uma frase, não um substantivo.
//
// FORTES — quem diz "meu objetivo" ou "vim para" está falando DE SI. O marcador
// já é auto-referente, e por isso vale sozinho.
const MARCADORES_FORTES_DE_OBJETIVO = [
  'meu objetivo (?:é|eh|e)',
  'meu foco (?:é|eh|e)',
  'minha meta (?:é|eh|e)',
  'estou aqui para',
  'vim para',
  'vim aqui para',
]

// FRACOS — `quero`, `preciso` e irmãos abrem um PEDIDO do dia a dia ("quero ver
// o PL do Ilo") exatamente como abrem uma apresentação. Sozinhos eles não
// bastam: `extrairApresentacao` só os aceita quando o texto traz também nome ou
// papel declarado, ou quando a apresentação foi PEDIDA (primeiro contato).
// Sem essa separação, toda tarefa pedida à IA virava "objetivo do seu perfil".
const MARCADORES_FRACOS_DE_OBJETIVO = [
  'gostaria de',
  'queria',
  'quero',
  'preciso de',
  'preciso',
  'busco',
  'procuro',
]

// ★ ABERTURAS DE PERGUNTA — o filtro que separa "vim para migrar clientes" de
// "o que preciso fazer hoje". O `?` resolveria os dois casos se a pontuação
// fosse confiável; ela não é, e quem mais sofre com isso é justamente quem
// FALA: a transcrição de fala quase nunca devolve o sinal de interrogação.
//
// ⚠️ `como` e `que` ficam DE FORA de propósito: "Como assessor de
// investimentos, quero..." é apresentação, e uma abertura frouxa aqui apagaria
// a frase inteira. Quem tem `?` já é pego pela primeira régua.
const ABERTURAS_INTERROGATIVAS = [
  'o que', 'o q', 'qual', 'quais', 'quando', 'quanto', 'quantos', 'quanta', 'quantas',
  'onde', 'aonde', 'quem', 'por que', 'porque', 'pra que', 'para que', 'sera que',
  'tem como', 'da para', 'de onde',
]

// O objetivo precisa de SUBSTÂNCIA: ou três palavras, ou vinte caracteres.
// "fazer hoje" e "migrar uma" têm duas palavras e dez caracteres — são o começo
// de um objetivo, não um. A alternativa por tamanho existe para o texto longo
// sem espaço (um colado de outra janela), que é blob, não fragmento.
const MIN_PALAVRAS_DO_OBJETIVO = 3
const MIN_CHARS_DO_OBJETIVO = 20

/** Espaços normalizados e pontas limpas — nunca `null`, sempre string. */
function limpar(valor) {
  return String(valor === undefined || valor === null ? '' : valor)
    .replace(/\s+/g, ' ')
    .trim()
}

/** Corta no teto do contrato, sem deixar reticências fingindo texto. */
function cortar(valor) {
  const limpo = limpar(valor)
  return limpo.length > MAX_CHARS_DO_VALOR ? limpo.slice(0, MAX_CHARS_DO_VALOR).trim() : limpo
}

/**
 * O PRIMEIRO nome — a mesma régua da saudação da spec-008 ([T062]): "Olá,
 * João Gabriel John" é um formulário lido em voz alta. Sem nome, devolve `''`
 * e o cartão sai sem nome — nunca um placeholder.
 */
export function primeiroNome(nomeCompleto) {
  const limpo = limpar(nomeCompleto)
  if (!limpo) return ''
  return limpo.split(' ')[0]
}

/** O trecho casa com um papel conhecido? Compara sem acento/caixa. */
function ehPapel(trecho) {
  const comparavel = normalizeText(limpar(trecho))
  for (let i = 0; i < PAPEIS.length; i += 1) {
    if (comparavel === PAPEIS[i] || comparavel.indexOf(PAPEIS[i] + ' ') === 0) return true
  }
  return false
}

/**
 * O papel declarado, com o qualificador quando houver.
 *
 * Varre a lista inteira e fica com o MAIOR casamento: `socio` e `socia` são
 * prefixos de nada, mas `consultor` é prefixo de `consultora` — parar no
 * primeiro devolveria "consultor" para quem disse "consultora".
 */
function extrairPapel(texto) {
  let achado = ''
  for (let i = 0; i < PAPEIS.length; i += 1) {
    const regex = new RegExp('\\b(' + PAPEIS[i] + QUALIFICADOR + ')\\b', 'i')
    const casou = regex.exec(texto)
    if (casou && casou[1].length > achado.length) achado = casou[1]
  }
  return cortar(achado)
}

/** O primeiro nome capturado por um grupo de marcadores, ou `''`. */
function buscarNome(texto, marcadores, exigeMaiuscula) {
  // ⚠️ A classe de caracteres precisa cobrir as duas caixas mesmo no grupo
  // FRACO: o regex roda com a flag `i` (o marcador tem de casar "Sou" e
  // "sou"), e sob `i` um `[A-ZÀ-Ý]` casaria minúscula assim mesmo — a régua
  // da maiúscula é conferida DEPOIS, no JS, onde ela de fato vale.
  const palavra = '[A-Za-zÀ-ÿ]+'
  for (let i = 0; i < marcadores.length; i += 1) {
    const regex = new RegExp(
      '\\b' + marcadores[i] + '\\s+(' + palavra + '(?:\\s+' + palavra + '){0,2})',
      'i'
    )
    const casou = regex.exec(texto)
    if (!casou) continue
    // O mesmo "sou" introduz papel; quem chegou aqui por ele não é nome.
    if (ehPapel(casou[1])) continue
    if (exigeMaiuscula && !/^[A-ZÀ-Ý]/.test(casou[1])) continue
    return cortar(casou[1])
  }
  return ''
}

/** O nome declarado no texto — ou `''`. Ver os dois grupos de marcadores. */
function extrairNome(texto) {
  return (
    buscarNome(texto, MARCADORES_FORTES_DE_NOME, false) ||
    buscarNome(texto, MARCADORES_FRACOS_DE_NOME, true)
  )
}

/**
 * O trecho tem substância de frase, ou é o começo de uma?
 *
 * ★ É esta régua que mata "fazer hoje" (o rabo da pergunta do owner) e
 * "migrar uma" (a hesitação do ditado). Ela roda ANTES de o marcador ser
 * aceito, e não depois: um marcador que só rendeu fragmento não conta como
 * achado — a busca continua nos outros.
 */
function temSubstancia(trecho) {
  const limpo = limpar(trecho)
  if (!limpo) return false
  if (limpo.length >= MIN_CHARS_DO_OBJETIVO) return true
  return limpo.split(' ').length >= MIN_PALAVRAS_DO_OBJETIVO
}

/** O primeiro objetivo COM SUBSTÂNCIA capturado por um grupo de marcadores. */
function buscarObjetivo(texto, marcadores) {
  for (let i = 0; i < marcadores.length; i += 1) {
    const regex = new RegExp('\\b' + marcadores[i] + '\\s+([^.!?\\n]+)', 'i')
    const casou = regex.exec(texto)
    if (!casou) continue
    const trecho = cortar(casou[1])
    if (temSubstancia(trecho)) return trecho
  }
  return ''
}

/**
 * O objetivo declarado e a FORÇA do marcador que o introduziu.
 *
 * A força viaja junto porque quem decide se ele basta é `extrairApresentacao`:
 * forte fala de si sozinho; fraco precisa de companhia. Devolver só a string
 * jogaria fora exatamente a informação que separa apresentação de pedido.
 *
 * @returns {{texto: string, forte: boolean}}
 */
function extrairObjetivo(texto) {
  const forte = buscarObjetivo(texto, MARCADORES_FORTES_DE_OBJETIVO)
  if (forte) return { texto: forte, forte: true }
  return { texto: buscarObjetivo(texto, MARCADORES_FRACOS_DE_OBJETIVO), forte: false }
}

/** As frases do texto, cada uma com a pontuação que a encerra. */
function frasesDoTexto(texto) {
  const frases = []
  // `+` no corpo garante casamento de tamanho ≥ 1 — sem ele o laço não avança.
  const regex = /[^.!?\n]+[.!?]*/g
  let casou = regex.exec(texto)
  while (casou) {
    if (limpar(casou[0])) frases.push(limpar(casou[0]))
    casou = regex.exec(texto)
  }
  return frases
}

/** A frase é uma pergunta? Pelo sinal, ou pela abertura quando ele não veio. */
function ehPergunta(frase) {
  if (frase.indexOf('?') !== -1) return true
  const comparavel = normalizeText(frase)
  for (let i = 0; i < ABERTURAS_INTERROGATIVAS.length; i += 1) {
    const abertura = ABERTURAS_INTERROGATIVAS[i]
    if (comparavel === abertura || comparavel.indexOf(abertura + ' ') === 0) return true
  }
  return false
}

/**
 * O texto SEM as frases interrogativas.
 *
 * ★ Descarta a FRASE, nunca o texto inteiro: quem se apresenta e emenda uma
 * pergunta ("Sou assessor. Quanto rendeu a carteira do Ilo?") não pode perder a
 * apresentação por causa da pergunta que veio depois.
 */
function semAsPerguntas(texto) {
  const afirmativas = []
  const frases = frasesDoTexto(texto)
  for (let i = 0; i < frases.length; i += 1) {
    if (!ehPergunta(frases[i])) afirmativas.push(frases[i])
  }
  return afirmativas.join(' ')
}

/**
 * A proposta de perfil, ou `null` quando não há o que propor.
 *
 * ★ **`null` é a regra que impede o cartão vazio.** Ele só nasce quando o
 * TEXTO trouxe ao menos um dos três campos: nome da sessão sozinho não conta,
 * senão toda mensagem da conversa acabaria pedindo confirmação de nada. Foi
 * exatamente essa regra que o cartão de 18/09 driblou — ele TINHA um campo, só
 * que arrancado de uma pergunta (ver o cabeçalho do arquivo).
 *
 * @param {string} texto O que o profissional escreveu ou falou.
 * @param {Object} [opcoes]
 * @param {string} [opcoes.nomeDaSessao] Nome de quem está logado — completa a
 *   proposta quando o texto não afirma um nome. Vem da SESSÃO, nunca do
 *   modelo (N21 da spec-008).
 * @param {boolean} [opcoes.apresentacaoPedida] A casa acabou de PEDIR a
 *   apresentação (o primeiro contato). Só aí um objetivo de marcador fraco
 *   ("quero…") vale sozinho: ali ele é resposta a uma pergunta sobre a pessoa,
 *   e não um pedido de tarefa no meio do trabalho.
 * @returns {?{nome: string, papel: string, objetivo: string}}
 */
export function extrairApresentacao(texto, { nomeDaSessao, apresentacaoPedida } = {}) {
  // ★ As perguntas caem AQUI, antes de tudo: nenhuma extração roda sobre elas.
  const limpo = semAsPerguntas(limpar(texto))
  if (!limpo) return null

  const nomeDoTexto = extrairNome(limpo)
  const papel = extrairPapel(limpo)
  const objetivo = extrairObjetivo(limpo)
  // Nome ou papel declarados são a prova de que a frase fala DA PESSOA — é o
  // que autoriza um objetivo de marcador fraco a entrar junto.
  const falouDeSi = Boolean(nomeDoTexto) || Boolean(papel)
  const objetivoVale =
    Boolean(objetivo.texto) && (objetivo.forte || falouDeSi || Boolean(apresentacaoPedida))
  if (!falouDeSi && !objetivoVale) return null

  return {
    nome: nomeDoTexto || primeiroNome(nomeDaSessao),
    papel: papel,
    objetivo: objetivoVale ? objetivo.texto : '',
  }
}

/**
 * Os `extras` que a proposta grava, sem as chaves vazias.
 *
 * Chave vazia não é omissão inofensiva: `extras` é SUBSTITUÍDO inteiro pelo
 * `PUT /memoria/perfil` (`usecase_memoria.perfil_set`, `substituir_extras=True`),
 * e gravar `objetivo: ''` deixaria na tela de Memória uma linha rotulada sem
 * conteúdo nenhum.
 */
export function extrasDaApresentacao(proposta) {
  const extras = {}
  if (!proposta) return extras
  if (limpar(proposta.nome)) extras[CHAVES_DA_APRESENTACAO.NOME] = cortar(proposta.nome)
  if (limpar(proposta.papel)) extras[CHAVES_DA_APRESENTACAO.PAPEL] = cortar(proposta.papel)
  if (limpar(proposta.objetivo)) extras[CHAVES_DA_APRESENTACAO.OBJETIVO] = cortar(proposta.objetivo)
  return extras
}
